import React, { useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import SvgIcon from "@mui/material/SvgIcon";
import InputAdornment from "@mui/material/InputAdornment";
import Alert from "@mui/material/Alert";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { useConceptsSearch } from "../../hooks";
import { DEFAULT_CONCEPTS_LIST, CONCEPT_CATEGORIES } from "./constants";
import type { ChecklistConceptItem, ConceptCategory } from "./types";

/** Add-to-selection icon for process mapping variant (outline circle + plus, #10B981). */
function ProcessMappingAddConceptIcon() {
  return (
    <SvgIcon viewBox="0 0 20 20" sx={{ width: 20, height: 20, flexShrink: 0 }}>
      <path
        d="M9 15H11V11H15V9H11V5H9V9H5V11H9V15ZM10 20C8.61667 20 7.31667 19.7375 6.1 19.2125C4.88333 18.6875 3.825 17.975 2.925 17.075C2.025 16.175 1.3125 15.1167 0.7875 13.9C0.2625 12.6833 0 11.3833 0 10C0 8.61667 0.2625 7.31667 0.7875 6.1C1.3125 4.88333 2.025 3.825 2.925 2.925C3.825 2.025 4.88333 1.3125 6.1 0.7875C7.31667 0.2625 8.61667 0 10 0C11.3833 0 12.6833 0.2625 13.9 0.7875C15.1167 1.3125 16.175 2.025 17.075 2.925C17.975 3.825 18.6875 4.88333 19.2125 6.1C19.7375 7.31667 20 8.61667 20 10C20 11.3833 19.7375 12.6833 19.2125 13.9C18.6875 15.1167 17.975 16.175 17.075 17.075C16.175 17.975 15.1167 18.6875 13.9 19.2125C12.6833 19.7375 11.3833 20 10 20ZM10 18C12.2333 18 14.125 17.225 15.675 15.675C17.225 14.125 18 12.2333 18 10C18 7.76667 17.225 5.875 15.675 4.325C14.125 2.775 12.2333 2 10 2C7.76667 2 5.875 2.775 4.325 4.325C2.775 5.875 2 7.76667 2 10C2 12.2333 2.775 14.125 4.325 15.675C5.875 17.225 7.76667 18 10 18Z"
        fill="#10B981"
      />
    </SvgIcon>
  );
}

/**
 * Left list rows use value-only `id`; selection state may store `parentCode:valueCode`
 * (from GET mapping_concepts). Hide row when either form matches a selected id.
 */
function isListConceptSelected(
  row: ChecklistConceptItem,
  selectedIds: ReadonlySet<string>,
): boolean {
  if (selectedIds.has(row.id)) return true;
  if (
    row.concept_code &&
    row.concept_code !== row.id &&
    selectedIds.has(`${row.concept_code}:${row.id}`)
  ) {
    return true;
  }
  for (const sid of selectedIds) {
    if (!sid.includes(":")) continue;
    const idx = sid.indexOf(":");
    const parent = sid.slice(0, idx);
    const valuePart = sid.slice(idx + 1);
    if (valuePart !== row.id) continue;
    if (!row.concept_code || row.concept_code === parent) return true;
  }
  return false;
}

export interface ConceptsSelectorValue {
  selectedConceptIds: string[];
  selectedConceptsDetails?: ChecklistConceptItem[];
}

export type ConceptsSelectorVariant = "standard" | "processMapping";

export interface ConceptsSelectorProps {
  value: ConceptsSelectorValue;
  onChange: (value: ConceptsSelectorValue) => void;
  /** Mock concepts list; when undefined, uses API (values/search) */
  conceptsList?: ChecklistConceptItem[];
  /** Maximum total selections across concepts + keywords (fallback 25). */
  maxTotalSelections?: number;
  /** Total selections across concepts + keywords (for enforcing combined max). */
  totalSelectionsCount: number;
  /** processMapping: layout and colors for Assessment Center process mapping dialog. */
  variant?: ConceptsSelectorVariant;
  /** When false, does not call values/search (use with lazy mount in process mapping). */
  enabled?: boolean;
}

export const ConceptsSelector: React.FC<ConceptsSelectorProps> = ({
  value,
  onChange,
  conceptsList,
  maxTotalSelections,
  totalSelectionsCount,
  variant = "standard",
  enabled = true,
}) => {
  const isProcessMapping = variant === "processMapping";
  const [conceptCategory, setConceptCategory] =
    useState<ConceptCategory>("All");
  const [conceptSearch, setConceptSearch] = useState("");

  const useApiConcepts = conceptsList === undefined;
  const loadMoreSentinelRef = useRef<HTMLDivElement>(null);
  const {
    concepts: apiConcepts,
    loading: conceptsLoading,
    loadingMore: conceptsLoadingMore,
    error: conceptsError,
    hasMore: conceptsHasMore,
    loadMore: conceptsLoadMore,
  } = useConceptsSearch({
    conceptCategory,
    conceptSearch,
    pageSize: 10,
    enabled: enabled && useApiConcepts,
  });

  // Use IntersectionObserver instead of scroll: when content is short and no scrollbar,
  // the sentinel at the bottom is visible and triggers load more.
  useEffect(() => {
    if (
      !enabled ||
      !useApiConcepts ||
      !conceptsHasMore ||
      conceptsLoadingMore ||
      conceptsLoading
    )
      return;
    const sentinel = loadMoreSentinelRef.current;
    const scrollRoot = sentinel?.parentElement;
    if (!sentinel || !scrollRoot) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) conceptsLoadMore();
      },
      { root: scrollRoot, rootMargin: "100px", threshold: 0 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [
    enabled,
    useApiConcepts,
    conceptsHasMore,
    conceptsLoadingMore,
    conceptsLoading,
    conceptsLoadMore,
    apiConcepts.length,
  ]);

  const conceptsListResolved = useApiConcepts
    ? apiConcepts
    : (conceptsList ?? DEFAULT_CONCEPTS_LIST);

  const selectedSet = useMemo(
    () => new Set(value.selectedConceptIds),
    [value.selectedConceptIds],
  );
  const selectedCount = selectedSet.size;

  const filteredConcepts = useMemo(() => {
    if (useApiConcepts) {
      return conceptsListResolved;
    }
    const byCategory =
      conceptCategory === "All"
        ? conceptsListResolved
        : conceptsListResolved.filter((c) => c.dimension === conceptCategory);
    if (!conceptSearch.trim()) return byCategory;
    const q = conceptSearch.trim().toLowerCase();
    return byCategory.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.dimension.toLowerCase().includes(q),
    );
  }, [useApiConcepts, conceptCategory, conceptSearch, conceptsListResolved]);

  const selectedConceptsByDimension = useMemo(() => {
    const details = value.selectedConceptsDetails ?? [];
    const detailsMap = new Map(details.map((c) => [c.id, c]));
    const map = new Map<string, ChecklistConceptItem[]>();
    for (const id of selectedSet) {
      const c = detailsMap.get(id) ?? conceptsListResolved.find((x) => x.id === id);
      const item: ChecklistConceptItem = c ?? {
        id,
        dimension: "Other",
        name: id,
      };
      const list = map.get(item.dimension) ?? [];
      list.push(item);
      map.set(item.dimension, list);
    }
    return Array.from(map.entries()).map(([title, values]) => ({
      title,
      values,
    }));
  }, [value.selectedConceptsDetails, conceptsListResolved, selectedSet]);

  const maxTotal = typeof maxTotalSelections === "number" ? maxTotalSelections : 25;

  const updateSelection = (
    id: string,
    add: boolean,
    item?: ChecklistConceptItem,
  ) => {
    const next = new Set(value.selectedConceptIds);
    let nextDetails = value.selectedConceptsDetails ?? [];
    if (add) {
      if (totalSelectionsCount >= maxTotal) return;
      next.add(id);
      const resolved =
        item ?? conceptsListResolved.find((x) => x.id === id);
      if (resolved) {
        nextDetails = nextDetails.filter((d) => d.id !== id);
        nextDetails.push(resolved);
      }
    } else {
      next.delete(id);
      nextDetails = nextDetails.filter((d) => d.id !== id);
    }
    onChange({
      selectedConceptIds: Array.from(next),
      selectedConceptsDetails: nextDetails,
    });
  };

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: isProcessMapping
          ? "minmax(220px, 240px) minmax(0, 1fr)"
          : "180px 1fr",
        gap: isProcessMapping ? 0 : 2,
        border: 1,
        borderColor: isProcessMapping ? "#E2E8F0" : "divider",
        borderRadius: isProcessMapping ? "12px" : 2,
        p: isProcessMapping ? 0 : 2,
        bgcolor: "white",
        overflow: "hidden",
        minHeight: isProcessMapping ? 400 : undefined,
        ...(isProcessMapping ? { fontSize: "13px" } : {}),
      }}
    >
      <Box
        sx={{
          borderRight: 1,
          borderColor: isProcessMapping ? "#E2E8F0" : "divider",
          pr: isProcessMapping ? 0 : 1.5,
          display: "flex",
          flexDirection: "column",
          gap: isProcessMapping ? 0 : 0.5,
          bgcolor: isProcessMapping ? "rgba(248, 250, 252, 0.92)" : "transparent",
        }}
      >
        {CONCEPT_CATEGORIES.map((cat) => (
          <Button
            key={cat}
            className="concepts-category-nav-btn"
            fullWidth
            size="small"
            variant="text"
            color="inherit"
            onClick={() => setConceptCategory(cat)}
            sx={{
              justifyContent: isProcessMapping ? "space-between" : "flex-start",
              textTransform: "none",
              fontSize: isProcessMapping ? "14px" : "0.875rem",
              px: isProcessMapping ? "16px" : 1.5,
              py: isProcessMapping ? "12px" : 1,
              minHeight: 0,
              borderRadius: isProcessMapping ? 0 : undefined,
              ...(conceptCategory === cat
                ? isProcessMapping
                  ? {
                      bgcolor: (theme) => theme.palette.primary.main + "14",
                      color: "primary.main",
                      fontWeight: 500,
                      "& .MuiButton-label": { color: "primary.main" },
                      "& span": { color: "primary.main" },
                      "&:hover": {
                        bgcolor: (theme) => theme.palette.primary.main + "24",
                        color: "primary.main",
                        "& .MuiButton-label": { color: "primary.main" },
                        "& span": { color: "primary.main" },
                      },
                    }
                  : {
                      bgcolor: "rgba(126, 34, 206, 0.12)",
                      color: "#6b21a8",
                      fontWeight: 600,
                    }
                : {
                    color: isProcessMapping ? "#475569" : "text.secondary",
                    fontWeight: isProcessMapping ? 500 : undefined,
                    "&:hover": { bgcolor: isProcessMapping ? "rgba(248,250,252,0.6)" : "action.hover" },
                  }),
            }}
          >
            <span>{cat}</span>
            {isProcessMapping && cat !== "All" ? (
              <ChevronRightIcon
                sx={{
                  fontSize: 18,
                  opacity: conceptCategory === cat ? 1 : 0.5,
                  color: conceptCategory === cat ? "primary.main" : "#475569",
                }}
              />
            ) : null}
          </Button>
        ))}
      </Box>

        <Box
        sx={{
          display: "grid",
          gridTemplateColumns: isProcessMapping ? "minmax(0, 1fr) minmax(260px, 320px)" : "1fr 1fr",
          gap: isProcessMapping ? 0 : 2,
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            position: "relative",
            display: "flex",
            flexDirection: "column",
            gap: isProcessMapping ? 0 : 1.5,
            p: 0,
            borderRight: isProcessMapping ? "1px solid #E2E8F0" : "none",
            bgcolor: isProcessMapping ? "#FFFFFF" : undefined,
          }}
        >
          {useApiConcepts && conceptsLoadingMore && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                zIndex: 10,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "rgba(255, 255, 255, 0.85)",
                borderRadius: 2,
                border: 1,
                borderColor: "divider",
              }}
            >
              <CircularProgress size={32} sx={{ mb: 1 }} />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={isProcessMapping ? { fontSize: "0.8125rem" } : undefined}
              >
                Loading more...
              </Typography>
            </Box>
          )}

          {isProcessMapping ? (
            <Box
              sx={{
                px: "12px",
                pt: "12px",
                pb: "13px",
                width: "100%",
                boxSizing: "border-box",
                borderBottom: "1px solid #F1F5F9",
              }}
            >
              <TextField
                size="small"
                fullWidth
                placeholder="Search values"
                value={conceptSearch}
                onChange={(e) => setConceptSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                    </InputAdornment>
                  ),
                  sx: {
                    bgcolor: "#F8FAFC",
                    borderRadius: "8px",
                  },
                }}
                sx={{
                  maxWidth: "100%",
                  m: 0,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#E2E8F0",
                  },
                  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#E2E8F0",
                  },
                  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "primary.main",
                    borderWidth: "1px",
                  },
                  "& .MuiOutlinedInput-root.Mui-focused": {
                    boxShadow: "none",
                  },
                  "& .MuiInputBase-input": {
                    fontSize: "14px",
                    fontFamily: '"Nunito Sans", sans-serif',
                  },
                  "& .MuiInputBase-input::placeholder": {
                    color: "#6B7280",
                    opacity: 1,
                  },
                }}
              />
            </Box>
          ) : (
            <TextField
              size="small"
              placeholder="Search values"
              value={conceptSearch}
              onChange={(e) => setConceptSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                  </InputAdornment>
                ),
                sx: {
                  bgcolor: "grey.50",
                },
              }}
              sx={{ maxWidth: "100%" }}
            />
          )}

          <Box
            sx={{
              border: isProcessMapping ? "none" : 1,
              borderTop: isProcessMapping ? "none" : undefined,
              borderColor: isProcessMapping ? undefined : "divider",
              borderRadius: isProcessMapping ? 0 : 2,
              p: isProcessMapping ? 0 : 1,
              bgcolor: isProcessMapping ? "#FFFFFF" : "grey.50",
              height: isProcessMapping ? 320 : 300,
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              gap: isProcessMapping ? 0 : 0.5,
            }}
          >
            {conceptsError && (
              <Alert
                severity="error"
                sx={{
                  mb: 1,
                  ...(isProcessMapping
                    ? { fontSize: "0.8125rem", py: 0.5, alignItems: "center" }
                    : {}),
                }}
              >
                {conceptsError}
              </Alert>
            )}
            {conceptsLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <>
                {filteredConcepts
                  .filter((c) => !isListConceptSelected(c, selectedSet))
                  .map((c) => (
                    <Button
                      key={c.id}
                      fullWidth
                      size="small"
                      disabled={totalSelectionsCount >= maxTotal}
                      onClick={() => updateSelection(c.id, true, c)}
                      sx={{
                        justifyContent: "space-between",
                        textTransform: "none",
                        fontSize: isProcessMapping ? "0.8125rem" : "0.875rem",
                        color: "text.primary",
                        alignItems: isProcessMapping ? "center" : undefined,
                        ...(isProcessMapping
                          ? {
                              whiteSpace: "normal",
                              minHeight: "unset",
                              height: "auto",
                            }
                          : {}),
                        py: isProcessMapping ? "12px" : undefined,
                        px: isProcessMapping ? "16px" : undefined,
                        borderRadius: isProcessMapping ? 0 : undefined,
                        borderBottom: isProcessMapping ? "1px solid #F8FAFC" : undefined,
                        border: isProcessMapping ? "none" : undefined,
                        "&:hover": {
                          bgcolor: isProcessMapping ? "#F8FAFC" : "background.paper",
                        },
                        "&.Mui-disabled": { opacity: 0.7 },
                      }}
                    >
                      {isProcessMapping ? (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            flex: 1,
                            minWidth: 0,
                            textAlign: "left",
                          }}
                        >
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                color: "#334155",
                                lineHeight: "20px",
                                display: "block",
                                fontSize: "14px",
                                fontFamily: '"Nunito Sans", sans-serif',
                                whiteSpace: "normal",
                                overflowWrap: "anywhere",
                                wordBreak: "break-word",
                              }}
                            >
                              {c.name}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                display: "block",
                                mt: "2px",
                                color: "#94A3B8",
                                letterSpacing: "-0.5px",
                                textTransform: "uppercase",
                                fontWeight: 400,
                                fontSize: "10px",
                                lineHeight: "15px",
                                fontFamily: '"Nunito Sans", sans-serif',
                              }}
                            >
                              {c.dimension}
                            </Typography>
                          </Box>
                          {totalSelectionsCount < maxTotal && (
                            <Box sx={{ flexShrink: 0, alignSelf: "center" }}>
                              <ProcessMappingAddConceptIcon />
                            </Box>
                          )}
                        </Box>
                      ) : (
                        <>
                          <span
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              flex: 1,
                              minWidth: 0,
                              justifyContent: "flex-start",
                            }}
                          >
                            <Chip
                              label={c.dimension}
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: "0.75rem",
                                flexShrink: 0,
                              }}
                              variant="outlined"
                              color="default"
                            />
                            <Typography
                              component="span"
                              variant="body2"
                              sx={{
                                flex: 1,
                                minWidth: 0,
                                whiteSpace: "normal",
                                overflowWrap: "anywhere",
                                wordBreak: "break-word",
                                lineHeight: 1.3,
                                py: 0.25,
                              }}
                            >
                              {c.name}
                            </Typography>
                          </span>
                          {totalSelectionsCount < maxTotal && (
                            <AddIcon
                              sx={{
                                fontSize: 16,
                                color: "success.main",
                                flexShrink: 0,
                              }}
                            />
                          )}
                        </>
                      )}
                    </Button>
                  ))}
                {useApiConcepts && conceptsHasMore && !conceptsLoading && (
                  <div
                    ref={loadMoreSentinelRef}
                    style={{ height: 1, minHeight: 1, visibility: "hidden" }}
                    aria-hidden="true"
                  />
                )}
              </>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            border: isProcessMapping ? "none" : 1,
            borderColor: "divider",
            borderRadius: isProcessMapping ? 0 : 2,
            pl: isProcessMapping ? "17px" : undefined,
            pr: isProcessMapping ? "16px" : undefined,
            py: isProcessMapping ? "16px" : undefined,
            p: isProcessMapping ? undefined : 1.5,
            bgcolor: isProcessMapping ? "#F8FAFC" : "grey.50",
            borderLeft: isProcessMapping ? "1px solid #E2E8F0" : undefined,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              mb: isProcessMapping ? 3 : 1.5,
              fontSize: isProcessMapping ? 12 : "0.875rem",
              lineHeight: isProcessMapping ? "16px" : undefined,
              letterSpacing: isProcessMapping ? "1.2px" : undefined,
              textTransform: isProcessMapping ? "capitalize" : undefined,
              color: isProcessMapping ? "#94A3B8" : "text.secondary",
              fontFamily: isProcessMapping ? '"Nunito Sans", sans-serif' : undefined,
            }}
          >
            Selected Values
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              maxHeight: isProcessMapping ? 320 : 300,
              overflowY: "auto",
            }}
          >
            {selectedCount === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  py: isProcessMapping ? 5 : 4,
                  px: isProcessMapping ? 0 : 2,
                }}
              >
                {isProcessMapping ? (
                  <DescriptionOutlinedIcon
                    sx={{ fontSize: 48, color: "#CBD5E1", mb: 2 }}
                  />
                ) : null}
                <Typography
                  variant="body2"
                  color="text.secondary"
                  textAlign="center"
                  sx={{
                    fontSize: isProcessMapping ? 14 : "0.875rem",
                    maxWidth: 280,
                    lineHeight: "20px",
                    color: isProcessMapping ? "#94A3B8" : undefined,
                    fontFamily: isProcessMapping ? '"Nunito Sans", sans-serif' : undefined,
                  }}
                >
                  Selected values will appear here grouped by dimension
                </Typography>
              </Box>
            ) : (
              selectedConceptsByDimension.map(({ title, values }) => (
                <Box
                  key={title}
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    p: isProcessMapping ? 1 : 2,
                    backgroundColor: "white",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "999px",
                        backgroundColor: "#a855f7",
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        ...(isProcessMapping ? { fontSize: "0.8125rem" } : {}),
                      }}
                    >
                      {title}
                    </Typography>
                    <Chip
                      size="small"
                      label={values.length}
                      sx={{
                        ml: "auto",
                        height: isProcessMapping ? 18 : 20,
                        ...(isProcessMapping
                          ? { fontSize: "0.6875rem", "& .MuiChip-label": { px: 0.75 } }
                          : {}),
                      }}
                    />
                  </Box>
                  {values.length === 0 ? (
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        fontStyle: "italic",
                        ...(isProcessMapping ? { fontSize: "0.75rem" } : {}),
                      }}
                    >
                      No values configured
                    </Typography>
                  ) : (
                    values.map((c) => (
                      <Box
                        key={c.id}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          "&:hover .delete-btn": { opacity: 1 },
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            flex: 1,
                            minWidth: 0,
                            whiteSpace: "normal",
                            overflowWrap: "anywhere",
                            wordBreak: "break-word",
                            ...(isProcessMapping ? { fontSize: "0.75rem" } : {}),
                          }}
                        >
                          • {c.name}
                        </Typography>
                        <Button
                          className="delete-btn"
                          variant="contained"
                          color="primary"
                          disableElevation
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            updateSelection(c.id, false);
                          }}
                          sx={{
                            minWidth: 0,
                            minHeight: 24,
                            p: 0.25,
                            opacity: 0,
                            transition: "opacity 0.2s",
                            boxShadow: "none",
                            "&:hover": {
                              boxShadow: "none",
                            },
                          }}
                          aria-label={`Remove ${c.name}`}
                        >
                          <CloseIcon sx={{ fontSize: 14 }} />
                        </Button>
                      </Box>
                    ))
                  )}
                </Box>
              ))
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

